import { format } from 'date-fns';
import type { Lesson } from '../types';
import { generateClinicOccurrences, parseLocalDateYmd } from './lessonDates';

export type CampSessionDateLesson = Pick<
  Lesson,
  'lessonType' | 'campSessionDates' | 'startDate' | 'endDate' | 'daysOfWeek' | 'startTime' | 'endTime'
>;

/** Upcoming bookable camp session dates (YYYY-MM-DD), matching public session picker logic. */
export function getUpcomingCampSessionDateStrs(
  lesson: CampSessionDateLesson,
  now: Date = new Date()
): string[] {
  if (lesson.lessonType !== 'camp') return [];

  const today = new Date(now);
  today.setHours(0, 0, 0, 0);

  if (lesson.campSessionDates?.length) {
    return lesson.campSessionDates
      .map((d) => parseLocalDateYmd(d))
      .filter((d): d is Date => d !== null && d >= today)
      .sort((a, b) => a.getTime() - b.getTime())
      .map((date) => format(date, 'yyyy-MM-dd'));
  }

  if (lesson.startDate && lesson.endDate) {
    return generateClinicOccurrences(lesson as Lesson, {
      startDate: lesson.startDate,
      endDate: lesson.endDate,
    })
      .filter((occ) => occ.status === 'scheduled' && occ.date >= today)
      .map((occ) => format(occ.date, 'yyyy-MM-dd'));
  }

  return [];
}

/** True when every selectable camp day is selected (full-week / full-camp package pricing). */
export function isCampFullPackageFromDaySelection(
  lesson: CampSessionDateLesson,
  selectedDates: readonly string[],
  unavailableDates: readonly string[] = [],
  now: Date = new Date()
): boolean {
  const upcoming = getUpcomingCampSessionDateStrs(lesson, now);
  const unavailable = new Set(unavailableDates);
  const selectable = upcoming.filter((d) => !unavailable.has(d));
  if (selectable.length === 0) return false;

  const selected = new Set(selectedDates);
  return selectable.length === selected.size && selectable.every((d) => selected.has(d));
}
